import { useContext, useEffect } from "react";
import { useAuthContext, AuthConfigContext } from "./provider";
import type { User, AuthActions, AuthConfig } from "./types";

/** Returns auth actions: login, logout, register, loginWithPassport, verifyMfa, challengeMfa */
export function useAuth(): AuthActions {
  const { login, logout, register, loginWithPassport, verifyMfa, challengeMfa } = useAuthContext();
  return { login, logout, register, loginWithPassport, verifyMfa, challengeMfa };
}

/** Returns the current user or null */
export function useUser(): User | null {
  const { user } = useAuthContext();
  return user;
}

/** Returns the AuthConfig passed to AuthProvider */
export function useAuthConfig(): AuthConfig {
  const config = useContext(AuthConfigContext);
  if (!config) {
    throw new Error("useAuthConfig must be used within an AuthProvider");
  }
  return config;
}

/** Redirects to login if not authenticated. Returns the authenticated user. */
export function useRequireAuth(): User {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const config = useAuthConfig();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      config.navigate(config.loginPath ?? "/login");
    }
  }, [isLoading, isAuthenticated, config]);

  // Return user if available; callers should handle the loading state
  if (!user) {
    // Returning a sentinel while loading/redirecting — callers must guard with isLoading
    return { id: "", email: "", roles: [], productRoles: [], isAuthenticated: false } as User;
  }
  return user;
}

/** Requires auth + a specific role. Redirects to forbidden page if role is missing. */
export function useRequireRole(role: string): User {
  const user = useRequireAuth();
  const config = useAuthConfig();

  useEffect(() => {
    if (user.id && !user.roles.includes(role)) {
      config.navigate(config.forbiddenPath ?? "/forbidden");
    }
  }, [user, role, config]);

  return user;
}

/** Requires auth + a role for a specific product. Redirects to forbidden page if not authorized. */
export function useRequireProductRole(product: string, role: string): User {
  const user = useRequireAuth();
  const config = useAuthConfig();

  useEffect(() => {
    if (user.id && !user.productRoles.some((r) => r.product === product && r.role === role)) {
      config.navigate(config.forbiddenPath ?? "/forbidden");
    }
  }, [user, product, role, config]);

  return user;
}

/** Requires auth + access to a specific product (any role). Redirects to forbidden page if not authorized. */
export function useRequireProductAccess(product: string): User {
  const user = useRequireAuth();
  const config = useAuthConfig();

  useEffect(() => {
    if (user.id && !user.productRoles.some((r) => r.product === product)) {
      config.navigate(config.forbiddenPath ?? "/forbidden");
    }
  }, [user, product, config]);

  return user;
}
